import { useState, useEffect } from "react";
import { api } from "../utils/api";
import {
  Search, Eye, Clock, CheckCircle2, XCircle, Bike, Loader2,
  UtensilsCrossed, ShoppingBasket, Package, X, Check, RefreshCw,
  MapPin, Phone, IndianRupee, User, ChevronRight,
} from "lucide-react";

const T = {
  primary:"#f97316", primaryDark:"#ea580c", primaryLight:"#fff7ed", primaryMid:"#fed7aa",
  white:"#ffffff", bg:"#f1f5f9", text:"#0f172a", textSub:"#64748b", textMuted:"#94a3b8",
  border:"#e2e8f0", green:"#16a34a", greenLight:"#dcfce7", red:"#ef4444", redLight:"#fef2f2",
  yellow:"#f59e0b", yellowLight:"#fefce8", blue:"#2563eb", blueLight:"#eff6ff",
  purple:"#7c3aed", purpleLight:"#f5f3ff",
};

const STATUS_FLOW = ["placed","accepted","preparing","packing","packed","ready","picked_up","on-the-way","delivered"];

const STATUS_MAP = {
  placed:       { label:"Placed",       bg:T.blueLight,    color:T.blue,    icon:Clock        },
  pending:      { label:"Placed",       bg:T.blueLight,    color:T.blue,    icon:Clock        },
  accepted:     { label:"Accepted",     bg:T.yellowLight,  color:T.yellow,  icon:CheckCircle2 },
  preparing:    { label:"Preparing",    bg:T.primaryLight, color:T.primary, icon:Loader2      },
  packing:      { label:"Packing",      bg:T.primaryLight, color:T.primary, icon:Loader2      },
  packed:       { label:"Packed",       bg:"#ecfeff",      color:"#0891b2", icon:Package      },
  ready:        { label:"Ready",        bg:"#ecfeff",      color:"#0891b2", icon:Package      },
  picked_up:    { label:"Picked Up",    bg:T.purpleLight,  color:T.purple,  icon:Bike         },
  "on-the-way": { label:"On the Way",   bg:"#fef9c3",      color:"#ca8a04", icon:Bike         },
  delivered:    { label:"Delivered",    bg:T.greenLight,   color:T.green,   icon:CheckCircle2 },
  cancelled:    { label:"Cancelled",    bg:T.redLight,     color:T.red,     icon:XCircle      },
};

// ── Detect order type ──────────────────────────────
function getOrderType(order) {
  if (order.orderType === "grocery" || order.serviceType === "grocery" || order.groceryStore || order.store) return "grocery";
  if (order.serviceType === "courier") return "courier";
  return "food";
}

// ── Vendor name — restaurant or grocery store ──────
function getVendorName(order) {
  if (order.restaurant?.name) return order.restaurant.name;
  if (order.groceryStore?.name) return order.groceryStore.name;
  if (order.store?.name)      return order.store.name;
  return "—";
}

// ── Service type badge ─────────────────────────────
function ServiceBadge({ order }) {
  const type = getOrderType(order);
  const map = {
    food:    { label:"Food",    bg:"#fff7ed", color:"#ea580c", Icon:UtensilsCrossed },
    grocery: { label:"Grocery", bg:"#f0fdf4", color:"#16a34a", Icon:ShoppingBasket  },
    courier: { label:"Courier", bg:"#eff6ff", color:"#2563eb", Icon:Package         },
  };
  const { label, bg, color, Icon } = map[type] || map.food;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:bg, color, fontSize:"0.62rem", fontWeight:700, padding:"2px 7px", borderRadius:100 }}>
      <Icon size={9}/> {label}
    </span>
  );
}

// ── Order Detail Modal ─────────────────────────────
function OrderDetail({ order, onClose, onStatusChange }) {
  const [updating, setUpdating] = useState(false);
  const type       = getOrderType(order);
  const vendorName = getVendorName(order);
  const canProgress = order.status !== "delivered" && order.status !== "cancelled";
  const nextStatus  = STATUS_FLOW[STATUS_FLOW.indexOf(order.status) + 1];
  const s = STATUS_MAP[order.status] || STATUS_MAP.placed;
  const SIcon = s.icon;

  const handleProgress = async () => {
    if (!nextStatus) return;
    setUpdating(true);
    const res = await api.orders.updateStatus(order._id, { status: nextStatus });
    setUpdating(false);
    if (res.success) onStatusChange(order._id, nextStatus);
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;
    setUpdating(true);
    const res = await api.orders.cancel(order._id, { reason:"Admin cancelled", cancelledBy:"admin" });
    setUpdating(false);
    if (res.success) onStatusChange(order._id, "cancelled");
  };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:400, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:T.white, borderRadius:20, width:"100%", maxWidth:500, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.15)" }}>

        {/* Header */}
        <div style={{ padding:"16px 18px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, background:T.white, zIndex:1 }}>
          <div>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1rem" }}>
              Order #{order._id?.slice(-6).toUpperCase()}
            </div>
            <div style={{ fontSize:"0.7rem", color:T.textMuted, marginTop:2 }}>
              {new Date(order.createdAt).toLocaleString("en-IN", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })}
            </div>
          </div>
          <button onClick={onClose} style={{ background:T.bg, border:"none", borderRadius:"50%", width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
            <X size={15} color={T.textSub}/>
          </button>
        </div>

        <div style={{ padding:18 }}>

          {/* Status + Service type */}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
            <span style={{ display:"inline-flex", alignItems:"center", gap:5, background:s.bg, color:s.color, fontWeight:700, fontSize:"0.8rem", padding:"5px 14px", borderRadius:100 }}>
              <SIcon size={12}/> {s.label}
            </span>
            <ServiceBadge order={order}/>
            <span style={{ fontSize:"0.7rem", color:T.textMuted, marginLeft:"auto" }}>
              {order.paymentMode?.toUpperCase()} · ₹{order.total}
            </span>
          </div>

          {/* Vendor — Restaurant OR Grocery Store */}
          <div style={{ background: type === "grocery" ? "#f0fdf4" : T.primaryLight, border:`1px solid ${type==="grocery"?"#bbf7d0":T.primaryMid}`, borderRadius:12, padding:"13px 14px", marginBottom:12 }}>
            <div style={{ fontSize:"0.62rem", fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"1px", marginBottom:6 }}>
              {type === "grocery" ? "Grocery Store" : "Restaurant"}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:36, height:36, borderRadius:10, background: type==="grocery" ? "#dcfce7" : T.primaryMid, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {type === "grocery"
                  ? <ShoppingBasket size={18} color="#16a34a"/>
                  : <UtensilsCrossed size={18} color={T.primaryDark}/>
                }
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:"0.88rem", color:T.text }}>{vendorName}</div>
                {(order.restaurant?.area || order.store?.area) && (
                  <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:"0.72rem", color:T.textSub, marginTop:2 }}>
                    <MapPin size={10}/> {order.restaurant?.area || order.store?.area}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Customer */}
          <div style={{ background:T.bg, borderRadius:12, padding:"13px 14px", marginBottom:12 }}>
            <div style={{ fontSize:"0.62rem", fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"1px", marginBottom:7 }}>Customer</div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:32, height:32, borderRadius:"50%", background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <span style={{ color:"#fff", fontWeight:800, fontSize:"0.82rem" }}>{order.user?.name?.charAt(0)||"?"}</span>
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:"0.85rem", color:T.text }}>{order.user?.name || "—"}</div>
                <div style={{ fontSize:"0.72rem", color:T.textSub }}>{order.user?.phone || "—"}</div>
              </div>
            </div>
            {order.deliveryAddress && (
              <div style={{ display:"flex", alignItems:"flex-start", gap:6, marginTop:10, paddingTop:10, borderTop:`1px solid ${T.border}` }}>
                <MapPin size={13} color={T.primary} style={{ flexShrink:0, marginTop:1 }}/>
                <div style={{ fontSize:"0.75rem", color:T.textSub, lineHeight:1.5 }}>
                  {order.deliveryAddress.line1 || order.deliveryAddress.address || order.deliveryAddress.street || "Address on file"}
                  {order.deliveryAddress.area && `, ${order.deliveryAddress.area}`}
                </div>
              </div>
            )}
          </div>

          {/* Rider info */}
          {order.rider?.name && (
            <div style={{ background:T.purpleLight, border:"1px solid #ddd6fe", borderRadius:12, padding:"13px 14px", marginBottom:12 }}>
              <div style={{ fontSize:"0.62rem", fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"1px", marginBottom:7 }}>Delivery Rider</div>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background:"linear-gradient(135deg,#7c3aed,#a855f7)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <Bike size={16} color="#fff"/>
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:"0.85rem", color:T.text }}>{order.rider.name}</div>
                  <div style={{ fontSize:"0.72rem", color:T.textSub }}>{order.rider.phone || "—"}</div>
                </div>
                {order.rider.autoAssigned && (
                  <span style={{ marginLeft:"auto", background:T.purpleLight, color:T.purple, fontSize:"0.6rem", fontWeight:700, padding:"2px 7px", borderRadius:100 }}>Auto-assigned</span>
                )}
              </div>
            </div>
          )}

          {/* Items */}
          <div style={{ background:T.bg, borderRadius:12, padding:"13px 14px", marginBottom:12 }}>
            <div style={{ fontSize:"0.62rem", fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"1px", marginBottom:10 }}>Items Ordered</div>
            {(order.items||[]).map((item, i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:i<order.items.length-1?8:0, paddingBottom:i<order.items.length-1?8:0, borderBottom:i<order.items.length-1?`1px dashed ${T.border}`:"none" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:20, height:20, borderRadius:"50%", background:T.primaryLight, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.65rem", fontWeight:800, color:T.primary, flexShrink:0 }}>{item.qty}x</div>
                  <span style={{ fontSize:"0.82rem", color:T.text }}>{item.name}</span>
                </div>
                <span style={{ fontSize:"0.82rem", fontWeight:700, color:T.text }}>₹{item.price * item.qty}</span>
              </div>
            ))}

            {/* Bill summary */}
            <div style={{ borderTop:`1px solid ${T.border}`, marginTop:12, paddingTop:12 }}>
              {[
                ["Subtotal",     order.subtotal],
                ["Delivery Fee", order.deliveryFee],
                ["Platform Fee", order.platformFee],
                ...(order.tip > 0 ? [["Tip", order.tip]] : []),
                ...(order.discount > 0 ? [["Discount", `-${order.discount}`]] : []),
              ].map(([k, v], i) => (
                <div key={i} style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                  <span style={{ fontSize:"0.75rem", color:T.textSub }}>{k}</span>
                  <span style={{ fontSize:"0.75rem", color:T.textSub }}>₹{v}</span>
                </div>
              ))}
              <div style={{ display:"flex", justifyContent:"space-between", marginTop:8, paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <span style={{ fontSize:"0.9rem", fontWeight:800, color:T.text }}>Total Paid</span>
                <span style={{ fontSize:"0.9rem", fontWeight:800, color:T.primary }}>₹{order.total}</span>
              </div>
            </div>
          </div>

          {/* Payment */}
          <div style={{ display:"flex", gap:10, marginBottom:16 }}>
            <div style={{ flex:1, background:T.bg, borderRadius:10, padding:"10px 12px" }}>
              <div style={{ fontSize:"0.6rem", color:T.textMuted, fontWeight:700, marginBottom:3 }}>PAYMENT MODE</div>
              <div style={{ fontWeight:700, fontSize:"0.82rem", textTransform:"uppercase", color:T.text }}>{order.paymentMode || "—"}</div>
            </div>
            <div style={{ flex:1, background:order.paymentStatus==="paid" ? T.greenLight : T.yellowLight, borderRadius:10, padding:"10px 12px" }}>
              <div style={{ fontSize:"0.6rem", color:T.textMuted, fontWeight:700, marginBottom:3 }}>PAYMENT STATUS</div>
              <div style={{ fontWeight:700, fontSize:"0.82rem", textTransform:"uppercase", color:order.paymentStatus==="paid" ? T.green : T.yellow }}>
                {order.paymentStatus || "pending"}
              </div>
            </div>
          </div>

          {/* Actions */}
          {canProgress && nextStatus && (
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={handleProgress} disabled={updating}
                style={{ flex:2, background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`, border:"none", borderRadius:12, padding:"12px", color:"#fff", fontWeight:800, fontSize:"0.85rem", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6, boxShadow:"0 4px 14px rgba(249,115,22,0.3)" }}>
                {updating ? <Loader2 size={15} className="spin"/> : <Check size={15}/>}
                Mark as {STATUS_MAP[nextStatus]?.label || nextStatus}
              </button>
              <button onClick={handleCancel} disabled={updating}
                style={{ flex:1, background:T.redLight, border:`1px solid #fecaca`, borderRadius:12, padding:"12px", color:T.red, fontWeight:700, fontSize:"0.82rem", cursor:"pointer", fontFamily:"inherit" }}>
                Cancel
              </button>
            </div>
          )}

          {/* Cancelled reason */}
          {order.status === "cancelled" && order.cancelReason && (
            <div style={{ background:T.redLight, border:"1px solid #fecaca", borderRadius:10, padding:"10px 14px", marginTop:12 }}>
              <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.red, marginBottom:3 }}>Cancellation Reason</div>
              <div style={{ fontSize:"0.78rem", color:T.red }}>{order.cancelReason}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Orders Page ───────────────────────────────
export default function Orders() {
  const [orders,     setOrders]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [filterStat, setFilterStat] = useState("all");
  const [filterType, setFilterType] = useState("all"); // food / grocery / all
  const [selected,   setSelected]   = useState(null);
  const [toast,      setToast]      = useState("");
  const [page,       setPage]       = useState(1);
  const [total,      setTotal]      = useState(0);

  const showToast = m => { setToast(m); setTimeout(() => setToast(""), 2200); };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit:15,
        ...(filterStat !== "all" ? { status: filterStat } : {}),
        ...(search ? { search } : {}),
      });
      const res = await api.orders.getAll(params.toString());
      if (res.success) { setOrders(res.data); setTotal(res.total || 0); }
    } catch { showToast("Backend connect nahi hua"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterStat, page]);

  const handleStatusChange = (id, newStatus) => {
    setOrders(o => o.map(x => x._id===id ? {...x, status:newStatus} : x));
    if (selected?._id === id) setSelected(s => ({...s, status:newStatus}));
    showToast(`Status updated to ${STATUS_MAP[newStatus]?.label || newStatus}!`);
  };

  // Client-side filter by type
  const filtered = orders.filter(o => {
    if (filterType === "food")    return getOrderType(o) === "food";
    if (filterType === "grocery") return getOrderType(o) === "grocery";
    return true;
  });

  // Stats
  const stats = {
    total:     orders.length,
    food:      orders.filter(o => getOrderType(o) === "food").length,
    grocery:   orders.filter(o => getOrderType(o) === "grocery").length,
    delivered: orders.filter(o => o.status === "delivered").length,
    cancelled: orders.filter(o => o.status === "cancelled").length,
  };

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}} @keyframes spin{to{transform:rotate(360deg)}}.spin{animation:spin 1s linear infinite}`}</style>

      {toast && (
        <div style={{ position:"fixed", top:80, left:"50%", transform:"translateX(-50%)", background:T.text, color:"#fff", padding:"10px 20px", borderRadius:100, fontSize:"0.82rem", fontWeight:600, zIndex:500, display:"flex", alignItems:"center", gap:7 }}>
          <Check size={14} color={T.green}/> {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.1rem", color:T.text }}>
            Orders {total > 0 && `(${total})`}
          </div>
          <div style={{ fontSize:"0.72rem", color:T.textMuted, marginTop:2 }}>Real-time from MongoDB</div>
        </div>
        <button onClick={load} style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:10, padding:"8px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:"0.78rem", fontWeight:600, color:T.textSub, fontFamily:"inherit" }}>
          <RefreshCw size={14} className={loading?"spin":""}/> Refresh
        </button>
      </div>

      {/* Stats cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))", gap:10, marginBottom:18 }}>
        {[
          { label:"All Orders",  val:total,           color:T.primary, bg:T.primaryLight },
          { label:"Food",        val:stats.food,      color:"#ea580c", bg:"#fff7ed",     Icon:UtensilsCrossed },
          { label:"Grocery",     val:stats.grocery,   color:"#16a34a", bg:"#f0fdf4",     Icon:ShoppingBasket  },
          { label:"Delivered",   val:stats.delivered, color:T.green,   bg:T.greenLight   },
          { label:"Cancelled",   val:stats.cancelled, color:T.red,     bg:T.redLight     },
        ].map((s, i) => {
          const Icon = s.Icon;
          return (
            <div key={i} style={{ background:s.bg, borderRadius:12, padding:"12px 14px", border:`1px solid ${s.color}22` }}>
              {Icon && <Icon size={14} color={s.color} style={{ marginBottom:6 }}/>}
              <div style={{ fontWeight:900, fontSize:"1.3rem", color:s.color, fontFamily:"'Fraunces',Georgia,serif" }}>{loading?"—":s.val}</div>
              <div style={{ fontSize:"0.68rem", color:T.textMuted, fontWeight:500, marginTop:2 }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Status filter tabs */}
      <div style={{ display:"flex", gap:6, overflowX:"auto", marginBottom:10, paddingBottom:4 }}>
        {["all","placed","accepted","preparing","ready","picked","on-the-way","delivered","cancelled"].map(s => (
          <button key={s} onClick={() => { setFilterStat(s); setPage(1); }}
            style={{ flexShrink:0, background:filterStat===s?T.primary:T.white, color:filterStat===s?"#fff":T.textSub, border:`1px solid ${filterStat===s?T.primary:T.border}`, borderRadius:100, padding:"6px 14px", fontWeight:700, fontSize:"0.74rem", cursor:"pointer", fontFamily:"inherit", textTransform:"capitalize", transition:"all .2s", whiteSpace:"nowrap" }}>
            {s==="all" ? "All" : STATUS_MAP[s]?.label || s}
          </button>
        ))}
      </div>

      {/* Type filter */}
      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        {[
          { id:"all",     label:"All Types",    icon:Package         },
          { id:"food",    label:"Food Orders",  icon:UtensilsCrossed },
          { id:"grocery", label:"Grocery",      icon:ShoppingBasket  },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setFilterType(t.id)}
              style={{ display:"flex", alignItems:"center", gap:5, background:filterType===t.id?T.text:T.white, color:filterType===t.id?"#fff":T.textSub, border:`1px solid ${filterType===t.id?T.text:T.border}`, borderRadius:100, padding:"6px 14px", fontWeight:600, fontSize:"0.74rem", cursor:"pointer", fontFamily:"inherit", transition:"all .2s" }}>
              <Icon size={12}/> {t.label}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ display:"flex", alignItems:"center", gap:8, background:T.white, border:`1.5px solid ${T.border}`, borderRadius:10, padding:"8px 12px", marginBottom:16, maxWidth:320 }}>
        <Search size={14} color={T.textMuted}/>
        <input placeholder="Search order ID or customer..." value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key==="Enter" && load()}
          style={{ border:"none", background:"transparent", color:T.text, fontSize:"0.83rem", outline:"none", fontFamily:"inherit", width:"100%" }}/>
      </div>

      {/* Table */}
      <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, overflow:"hidden" }}>
        {loading ? (
          <div style={{ padding:60, textAlign:"center", color:T.textMuted }}>
            <Loader2 size={28} color={T.primary} className="spin" style={{ marginBottom:10 }}/>
            <div>Loading orders...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:60, textAlign:"center", color:T.textMuted }}>
            <Package size={40} color={T.border} style={{ marginBottom:10 }}/>
            <div style={{ fontWeight:600 }}>No orders found</div>
          </div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:700 }}>
              <thead>
                <tr style={{ borderBottom:`1px solid ${T.border}`, background:T.bg }}>
                  {["Order ID","Type","Customer","Vendor","Amount","Payment","Status","Action"].map(h => (
                    <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:"0.6rem", fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"1px", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((o, i) => {
                  const st    = STATUS_MAP[o.status] || STATUS_MAP.placed;
                  const SIcon = st.icon;
                  const type  = getOrderType(o);
                  return (
                    <tr key={o._id}
                      style={{ borderBottom:`1px solid ${T.border}`, transition:"background .15s", animation:`fadeUp .3s ${i*.03}s ease both`, cursor:"pointer" }}
                      onMouseEnter={e => e.currentTarget.style.background = T.bg}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      onClick={() => setSelected(o)}>

                      {/* Order ID */}
                      <td style={{ padding:"12px 14px" }}>
                        <div style={{ fontSize:"0.78rem", fontWeight:700, color:T.primary }}>
                          #{o._id?.slice(-6).toUpperCase()}
                        </div>
                        <div style={{ fontSize:"0.65rem", color:T.textMuted, marginTop:2 }}>
                          {new Date(o.createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short" })}
                        </div>
                      </td>

                      {/* Type badge */}
                      <td style={{ padding:"12px 14px" }}>
                        <ServiceBadge order={o}/>
                      </td>

                      {/* Customer */}
                      <td style={{ padding:"12px 14px" }}>
                        <div style={{ fontWeight:700, fontSize:"0.82rem", color:T.text }}>{o.user?.name || "—"}</div>
                        <div style={{ fontSize:"0.68rem", color:T.textMuted }}>{o.user?.phone || ""}</div>
                      </td>

                      {/* Vendor — Restaurant or Grocery Store */}
                      <td style={{ padding:"12px 14px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <div style={{ width:26, height:26, borderRadius:8, background: type==="grocery" ? "#dcfce7" : T.primaryLight, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                            {type === "grocery"
                              ? <ShoppingBasket size={13} color="#16a34a"/>
                              : <UtensilsCrossed size={13} color={T.primaryDark}/>
                            }
                          </div>
                          <div>
                            <div style={{ fontSize:"0.78rem", fontWeight:700, color:T.text, maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {getVendorName(o)}
                            </div>
                            <div style={{ fontSize:"0.62rem", color:T.textMuted }}>
                              {type === "grocery" ? "Grocery Store" : "Restaurant"}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Amount */}
                      <td style={{ padding:"12px 14px", fontSize:"0.88rem", fontWeight:800, color:T.text }}>₹{o.total}</td>

                      {/* Payment */}
                      <td style={{ padding:"12px 14px", fontSize:"0.72rem", color:T.textSub, textTransform:"uppercase", fontWeight:600 }}>{o.paymentMode}</td>

                      {/* Status */}
                      <td style={{ padding:"12px 14px" }}>
                        <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:st.bg, color:st.color, fontSize:"0.68rem", fontWeight:700, padding:"3px 9px", borderRadius:100, whiteSpace:"nowrap" }}>
                          <SIcon size={10}/> {st.label}
                        </span>
                      </td>

                      {/* View */}
                      <td style={{ padding:"12px 14px" }}>
                        <button onClick={e => { e.stopPropagation(); setSelected(o); }}
                          style={{ background:T.primaryLight, border:"none", borderRadius:8, padding:"6px 12px", color:T.primary, fontWeight:700, fontSize:"0.72rem", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}>
                          <Eye size={13}/> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 15 && (
        <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:14 }}>
          <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
            style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:8, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit", fontSize:"0.78rem", color:T.textSub }}>Prev</button>
          <span style={{ padding:"7px 14px", fontSize:"0.78rem", color:T.textSub }}>Page {page} of {Math.ceil(total/15)}</span>
          <button onClick={() => setPage(p => p+1)} disabled={page>=Math.ceil(total/15)}
            style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:8, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit", fontSize:"0.78rem", color:T.textSub }}>Next</button>
        </div>
      )}

      {selected && (
        <OrderDetail
          order={selected}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}